import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, CheckCircle2, AlertTriangle, Circle, MinusCircle, Camera,
  ChevronDown, ChevronUp, Lock, Link2, Tag, RefreshCw, ListChecks,
} from "lucide-react";

const STATUS = {
  complete: { label: "Complete", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  needs_attention: { label: "Needs attention", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: AlertTriangle },
  missing_evidence: { label: "Missing evidence", cls: "bg-orange-50 text-orange-700 border-orange-200", Icon: Circle },
  not_started: { label: "Not started", cls: "bg-slate-100 text-slate-600 border-slate-200", Icon: Circle },
  no_applicable_views: { label: "No applicable views", cls: "bg-slate-50 text-slate-500 border-slate-200", Icon: MinusCircle },
};
const SLOT_STATE = { captured: "Captured", missing: "Missing", not_applicable: "Not applicable" };
const FLAG_LABEL = {
  needs_retake: "Needs retake", unsealed: "Unsealed",
  move_out_match_missing: "Move-out match missing", label_confirmation_needed: "Label confirmation needed",
};

export default function EvidenceCoverage() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async (ph) => {
    setLoading(true); setError(false);
    try {
      const { data } = await api.get(`/properties/${propertyId}/coverage${ph ? `?phase=${ph}` : ""}`);
      setData(data); setPhase(data.phase);
    } catch (e) {
      if (e?.response?.status === 404) { toast.error("Property not found."); navigate("/dashboard"); return; }
      setError(true);
    } finally { setLoading(false); }
  }, [propertyId, navigate]);

  useEffect(() => { load(); }, [load]);

  const markNA = async (roomId, slot, active) => {
    try {
      await api.post(`/properties/${propertyId}/coverage/override`, { phase, room_id: roomId, slot, active });
      await load(phase);
    } catch { toast.error("Couldn't update that view."); }
  };

  if (loading) return (
    <div className="min-h-screen grid place-items-center bg-[#F8FAFC]" data-testid="coverage-loading">
      <Loader2 className="animate-spin text-brand" size={28} />
    </div>
  );
  if (error) return (
    <div className="min-h-screen grid place-items-center bg-[#F8FAFC] p-6" data-testid="coverage-error">
      <div className="text-center">
        <AlertTriangle className="mx-auto text-amber-500" size={30} />
        <p className="mt-2 font-semibold text-navy">Coverage couldn’t load.</p>
        <Button data-testid="coverage-retry" onClick={() => load(phase)} className="mt-3 rounded-xl bg-navy text-white"><RefreshCw size={15} className="mr-1.5" /> Retry</Button>
      </div>
    </div>
  );

  const s = data.summary;
  const phases = data.available_phases || [phase];

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-20" data-testid="coverage-page">
      <header className="sticky top-0 z-10 bg-white border-b border-[#E2E8F0] px-4 py-3 flex items-center gap-2">
        <button data-testid="coverage-back" onClick={() => navigate(`/property/${propertyId}`)} className="text-navy"><ArrowLeft size={20} /></button>
        <div className="min-w-0">
          <h1 className="font-heading font-bold text-navy text-base leading-tight flex items-center gap-1.5"><ListChecks size={16} className="text-brand" /> Evidence Coverage</h1>
          <p className="text-[11px] text-[#64748B] truncate">{data.address}</p>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {phases.length > 1 && (
          <div className="flex gap-2" data-testid="coverage-phase-switch">
            {phases.map((p) => (
              <button key={p} data-testid={`coverage-phase-${p}`} onClick={() => load(p)}
                className={`flex-1 rounded-xl border px-3 py-2 text-sm font-semibold ${phase === p ? "border-brand bg-brand/10 text-brand" : "border-[#E2E8F0] bg-white text-[#64748B]"}`}>
                {p === "move_in" ? "Move-in" : "Move-out"}
              </button>
            ))}
          </div>
        )}

        {/* Summary */}
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4" data-testid="coverage-summary">
          {s.denominator_zero ? (
            <p className="text-sm text-[#475569]" data-testid="coverage-zero-denominator">
              <b>0 applicable recommended views.</b> Add rooms and capture recommended views, or all views here are marked Not applicable.
            </p>
          ) : (
            <>
              <p className="font-heading text-2xl font-bold text-navy" data-testid="coverage-headline">
                {s.captured_views} of {s.applicable_views} applicable views captured — {s.percentage}%
              </p>
              <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-brand" style={{ width: `${s.percentage}%` }} />
              </div>
            </>
          )}
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
            <Stat label="Rooms complete" value={s.rooms_complete} />
            <Stat label="Needs attention" value={s.rooms_needing_attention} />
            <Stat label="Missing evidence" value={s.rooms_missing_evidence} />
            <Stat label="Not started" value={s.rooms_not_started} />
            {s.quality_warning_photos > 0 && <Stat label="Quality warnings" value={s.quality_warning_photos} />}
            {s.unsealed_photos > 0 && <Stat label="Unsealed photos" value={s.unsealed_photos} />}
            {phase === "move_out" && <Stat label="Missing pairs" value={s.missing_pairs} />}
          </div>
          <p className="mt-3 text-[11px] text-[#94A3B8] leading-snug">{data.disclaimer}</p>
        </div>

        {/* Next actions */}
        {data.actions?.length > 0 && (
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4" data-testid="coverage-actions">
            <p className="font-heading font-bold text-navy text-sm">What to do next</p>
            <div className="mt-2 space-y-1.5">
              {data.actions.map((a, i) => (
                <button key={i} data-testid={`coverage-action-${i}`} onClick={() => navigate(a.link)}
                  className="w-full flex items-center gap-2 text-left rounded-xl border border-[#EEF2F6] bg-[#F8FAFC] hover:bg-slate-100 px-3 py-2">
                  <Camera size={14} className="text-brand shrink-0" />
                  <span className="text-xs text-[#334155] flex-1">{a.text}</span>
                  <ArrowLeft size={13} className="rotate-180 text-[#94A3B8]" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Room cards */}
        <div className="space-y-2.5">
          {data.rooms.map((r) => {
            const st = STATUS[r.status] || STATUS.not_started;
            const open = expanded[r.room_id];
            return (
              <div key={r.room_id} className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden" data-testid={`coverage-room-${r.room_id}`}>
                <button onClick={() => setExpanded((e) => ({ ...e, [r.room_id]: !open }))} className="w-full flex items-center gap-2 px-4 py-3 text-left" data-testid={`coverage-room-toggle-${r.room_id}`}>
                  <st.Icon size={18} className="shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-navy text-sm truncate">{r.name}</p>
                    {r.status !== "no_applicable_views" && (
                      <p className="text-[11px] text-[#64748B]">{r.captured_slots}/{r.applicable_slots} views{r.missing_pairs ? ` · ${r.missing_pairs} pair(s) missing` : ""}</p>
                    )}
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-full border ${st.cls}`} data-testid={`coverage-room-status-${r.room_id}`}>{st.label}</span>
                  {open ? <ChevronUp size={16} className="text-[#94A3B8]" /> : <ChevronDown size={16} className="text-[#94A3B8]" />}
                </button>
                {open && (
                  <div className="border-t border-[#F1F5F9] px-4 py-2 space-y-1.5" data-testid={`coverage-slots-${r.room_id}`}>
                    {r.slots.map((sl) => (
                      <div key={sl.slot} className="flex items-center gap-2 py-1" data-testid={`coverage-slot-${r.room_id}-${sl.slot}`}>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${sl.state === "captured" ? "bg-emerald-100 text-emerald-700" : sl.state === "missing" ? "bg-slate-100 text-slate-500" : "bg-slate-50 text-slate-400"}`}>
                          {SLOT_STATE[sl.state]}
                        </span>
                        <span className="text-xs text-[#334155] flex-1">{sl.label}</span>
                        <div className="flex flex-wrap gap-1 justify-end">
                          {(sl.flags || []).map((f) => (
                            <span key={f} className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-0.5" data-testid={`coverage-flag-${r.room_id}-${sl.slot}-${f}`}>
                              {f === "unsealed" ? <Lock size={9} /> : f === "label_confirmation_needed" ? <Tag size={9} /> : f === "move_out_match_missing" ? <Link2 size={9} /> : <AlertTriangle size={9} />}
                              {FLAG_LABEL[f]}
                            </span>
                          ))}
                          {sl.state !== "not_applicable" ? (
                            <button data-testid={`coverage-na-${r.room_id}-${sl.slot}`} onClick={() => markNA(r.room_id, sl.slot, true)} className="text-[9px] text-[#94A3B8] hover:underline">Mark N/A</button>
                          ) : (
                            <button data-testid={`coverage-restore-${r.room_id}-${sl.slot}`} onClick={() => markNA(r.room_id, sl.slot, false)} className="text-[9px] text-brand hover:underline">Restore</button>
                          )}
                        </div>
                      </div>
                    ))}
                    <Button data-testid={`coverage-room-capture-${r.room_id}`} size="sm" variant="outline" onClick={() => navigate(r.capture_link)} className="w-full mt-1 h-8 rounded-lg text-xs"><Camera size={13} className="mr-1" /> Open capture</Button>
                  </div>
                )}
              </div>
            );
          })}
          {data.rooms.length === 0 && (
            <div className="rounded-2xl border border-dashed border-[#CBD5E1] bg-white p-6 text-center" data-testid="coverage-empty">
              <Camera className="mx-auto text-[#94A3B8]" size={22} />
              <p className="mt-2 text-sm font-medium text-navy">No rooms yet</p>
              <p className="text-xs text-[#64748B]">Add rooms and capture photos to start building coverage.</p>
              <Button data-testid="coverage-empty-cta" onClick={() => navigate(`/property/${propertyId}`)} className="mt-3 rounded-xl bg-navy text-white text-sm">Go to property</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-[#F8FAFC] border border-[#EEF2F6] px-2.5 py-1.5">
      <p className="text-[10px] text-[#64748B]">{label}</p>
      <p className="font-bold text-navy text-base">{value}</p>
    </div>
  );
}
